import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import LoadCard from '../components/LoadCard';
import LoadSearchForm from '../components/LoadSearchForm';
import { Filter, RefreshCw, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fetchMarketLoads } from '@/lib/marketLoads';
import { useRealtimeLoads } from '@/hooks/use-realtime-loads';
import { checkBookingEligibility } from '@/lib/driverEligibility';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import PendingApprovalNotice from '@/components/PendingApprovalNotice';

export default function LoadBoard() {
  const { user, isStaff, carrierId } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [driverProfile, setDriverProfile] = useState(undefined);

  // Was `isAdmin = user?.role === 'admin'` (Base44-era check). In Supabase
  // mode, invited accounting/hr/dispatch staff have user.role='authenticated'
  // and their staff_role lives on staff_profiles. The unified `isStaff` from
  // AuthContext covers admin/hr/accounting/dispatch/super_admin -- using
  // the old check meant a non-admin staff member hitting / saw the
  // "Application Under Review" driver-onboarding screen meant for drivers.
  const isAdmin = isStaff;

  const activeLoad = loads.find(l =>
    (l.status === 'Booked' || l.status === 'In Transit') &&
    // Case-sensitive match missed a driver's active load whenever the
    // stored email casing didn't match auth's exactly (e.g. an
    // admin-entered address book / CSV import), leaving the driver
    // stuck on the load board with no way to reach their active trip.
    // Every sibling lookup (AdminPanel, DriverProfile) normalizes case.
    (l.assigned_driver_email || '').toLowerCase() === (user?.email || '').toLowerCase()
  );

  useEffect(() => {
    if (!user) return;
    if (isAdmin) { setDriverProfile(null); loadAll(); return; }
    findMyDriverProfile(user.email).then(profile => {
      setDriverProfile(profile);
      if (profile?.approval_status === 'Approved') loadAll();
    });
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    const [companyLoads, profile] = await Promise.all([
      client.entities.Load.filter({ status: 'Available' }, '-created_date', 100),
      !isAdmin && user?.email ? findMyDriverProfile(user.email).catch(() => null) : Promise.resolve(null),
    ]);
    const market = await fetchMarketLoads(
      profile?.address || 'Las Vegas, NV',
      // trailer_type drives freight matching post-split (DriverFormModal
      // 2026-05-28 refactor). Fall back to truck_type for drivers whose
      // record predates the split + still holds the old freight value
      // in truck_type. Final fallback keeps behaviour identical to
      // pre-refactor when neither field is set.
      profile?.trailer_type || profile?.truck_type || 'Reefer'
    );
    setLoads([
      ...companyLoads.map(l => ({ ...l, source: 'Company' })),
      ...market,
    ]);
    setHasSearched(false);
    setLoading(false);
  };

  useRealtimeLoads(carrierId, loadAll);

  const handleSearch = async ({ origin, destination, truckType, startDate, endDate, postAge }) => {
    setLoading(true);
    setHasSearched(true);

    // Build filter query
    const query = { status: 'Available' };

    if (truckType && truckType !== 'All') {
      query.equipment_required = truckType;
    }

    if (startDate) {
      query.pickup_date = { ...query.pickup_date, $gte: startDate };
    }
    if (endDate) {
      query.pickup_date = { ...query.pickup_date, $lte: endDate };
    }

    if (postAge) {
      const now = new Date();
      const hoursMap = { '2h': 2, '12h': 12, '24h': 24, '3d': 72 };
      const hours = hoursMap[postAge];
      if (hours) {
        const cutoff = new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString();
        query.created_date = { $gte: cutoff };
      }
    }

    // Fetch company loads + market loads in parallel
    const [companyData, marketData] = await Promise.all([
      client.entities.Load.filter(query, '-created_date', 200),
      fetchMarketLoads(
        driverProfile?.address || 'Las Vegas, NV',
        // trailer_type post-refactor; truck_type fallback for legacy data.
        driverProfile?.trailer_type || driverProfile?.truck_type || query.equipment_required || 'Reefer'
      ),
    ]);

    let data = [
      ...companyData.map(l => ({ ...l, source: 'Company' })),
      ...marketData,
    ];

    // Client-side partial match for origin / destination (case-insensitive, supports partial or state code)
    if (origin.trim()) {
      const term = origin.trim().toLowerCase();
      data = data.filter(l => l.origin?.toLowerCase().includes(term));
    }
    if (destination.trim()) {
      const term = destination.trim().toLowerCase();
      data = data.filter(l => l.destination?.toLowerCase().includes(term));
    }

    setLoads(data);
    setLoading(false);
  };

  const handleBook = async (load) => {
    // Guard: market loads must be booked through the broker
    if (load.source === 'Market') {
      toast.info('Market loads must be booked directly through the broker. Contact the broker using the details on the load card.');
      return;
    }

    // Guard: check if driver already has an active load
    const activeLoads = await client.entities.Load.filter(
      { assigned_driver_email: user.email },
      '-created_date',
      10
    );
    const hasActive = activeLoads.some(l => l.status === 'Booked' || l.status === 'In Transit');
    if (hasActive) {
      toast.error('You already have an active load. Complete or deliver your current load before booking a new one.');
      return;
    }

    // Guard: equipment attached (a truck assigned to this driver in
    // Assets) and a fully completed DQF -- same bar dispatchers are
    // held to when booking a driver manually.
    const myTrucks = await client.entities.Asset.filter({ assigned_driver_email: user.email, asset_type: 'Truck' }, '-created_date', 10);
    const { eligible, reasons } = checkBookingEligibility(driverProfile, user.email, myTrucks);
    if (!eligible) {
      toast.error(`You can't book this load yet — ${reasons.join(' ')}`, { duration: 6000 });
      return;
    }

    setBooking(load.id);
    // Snapshot which unit this driver is actually hauling with right now --
    // unit-level reports (UnitRevenueReport, UnitMilesReport, etc.) key off
    // this instead of inferring it later from the driver's CURRENT truck
    // assignment, which silently misattributes the load if that driver is
    // ever reassigned to a different truck afterward.
    const myActiveTruck = myTrucks.find(t => t.is_active !== false) || myTrucks[0];
    await client.entities.Load.update(load.id, {
      status: 'Booked',
      assigned_driver_email: user.email,
      // Prefer the DriverProfile's own full_name over user.full_name
      // (Supabase Auth metadata, frequently unset for driver accounts) --
      // this is what the Loads table, dispatch board, and every load
      // detail view show as the driver's identity from here on.
      assigned_driver_name: driverProfile?.full_name || user.full_name || user.email,
      truck_unit: myActiveTruck?.unit_number || undefined,
    });
    toast.success(`Load #${load.load_number} booked! Head to Active Load to manage it.`);
    await loadAll();
    setBooking(null);
  };

  const availableLoads = loads.filter(l => l.status === 'Available' || l.source === 'Market');

  // driverProfile starts `undefined` while its own fetch is in flight --
  // with no guard here, a non-admin driver briefly saw the full (empty)
  // Load Board render before this flipped to the pending-approval notice
  // once the fetch resolved, instead of going straight to whichever one
  // actually applies.
  if (!isAdmin && driverProfile === undefined) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Pending approval screen for non-admin drivers
  if (!isAdmin && driverProfile?.approval_status !== 'Approved') {
    return <PendingApprovalNotice status={driverProfile?.approval_status} />;
  }

  return (
    <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-space text-2xl font-bold text-foreground">Load Board</h1>

        </div>
        <Button variant="outline" onClick={loadAll} className="w-fit border-border hover:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Active Load Banner */}
      {activeLoad && (
        <div className="glass-card border-primary/40 p-4 flex items-center gap-3 gold-glow">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <p className="text-sm text-foreground">
            <span className="font-semibold text-primary">Active Load:</span> #{activeLoad.load_number} — {activeLoad.origin} → {activeLoad.destination}
          </p>
          <Link to="/active-load" className="ml-auto text-xs text-primary hover:underline">Manage →</Link>
        </div>
      )}

      {/* Advanced Search Form */}
      <LoadSearchForm onSearch={handleSearch} loading={loading} />

      {/* Stats */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Filter className="w-4 h-4" />
        <span>
          {availableLoads.length} load{availableLoads.length !== 1 ? 's' : ''} {hasSearched ? 'found' : 'available'}
        </span>
      </div>

      {/* Load Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="glass-card p-5 h-52 animate-pulse bg-white/3" />
          ))}
        </div>
      ) : availableLoads.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground font-medium">No loads found</p>
          <p className="text-muted-foreground text-sm mt-1">
            {hasSearched ? 'Try adjusting your search filters.' : 'Check back soon or refresh the board.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {availableLoads.map(load => (
            <LoadCard
              key={load.id}
              load={load}
              onBook={handleBook}
              isBooked={booking === load.id}
              currentUserLoad={activeLoad}
            />
          ))}
        </div>
      )}

      {/* AI Dispatcher used to mount here -- it's now rendered by
          Layout so chat state survives navigation across routes, not
          just within the Load Board. */}
    </div>
  );
}